/**
 * «Кандидат ждёт вас в комнате» в браузере: одна вкладка звенит, закрытое в
 * одной вкладке закрыто во всех, из живой встречи — только в новую вкладку.
 *
 * Зачем столько правил вокруг одной карточки. Рекрутёр держит CRM открытой в
 * трёх вкладках, и без общей памяти одно ожидание звенело бы трижды. А кнопка
 * «Войти в комнату», нажатая посреди другой встречи, увела бы вкладку из
 * комнаты и оборвала бы живой разговор: переход внутри приложения не
 * спрашивает «уйти со страницы?» (VideoInterviewRecruiterRoomPage.tsx).
 *
 * Хранилище — localStorage: оно общее у вкладок одного сайта и присылает
 * соседям событие `storage`. Его может не быть (приватное окно, запрет) — тогда
 * всё работает в пределах вкладки, просто без согласования с соседями.
 */
import type { CandidateWaitingPopup } from "@shared/candidateWaiting";
import {
  candidateWaitingSubtitle,
  candidateWaitingTitle,
} from "@shared/candidateWaiting";
import { isVideoLivePath } from "@/lib/videoRoomPath";

export type KeyValueStorage = Pick<Storage, "getItem" | "setItem">;

/** Закрытые крестиком (или по кнопке «Войти») периоды ожидания. */
export const WAITING_DISMISSED_KEY = "candidate-waiting-dismissed";
/** Какая вкладка уже позвонила по периоду ожидания. */
export const WAITING_ALERTED_KEY = "candidate-waiting-alerted";
/** Предложение включить уведомления на компьютере — один раз. */
export const WAITING_NOTIFY_OFFERED_KEY = "candidate-waiting-notify-offered";

/** Помним последние периоды: список нужен, чтобы не повторяться, а не расти. */
const MAX_REMEMBERED = 100;

/**
 * localStorage, если браузер его даёт. Приватное окно Safari и запрет на
 * хранение бросают исключение уже при обращении к свойству.
 */
export function browserStorage(): KeyValueStorage | null {
  try {
    return window.localStorage;
  } catch {
    // Без хранилища вкладка работает сама по себе, без согласования с соседями.
    return null;
  }
}

/** Метка вкладки: кто из соседей уже позвонил. Криптостойкость не нужна. */
export function newTabId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Компьютер с мышью, а не телефон: предлагать уведомления есть смысл только там. */
export function isDesktopPointer(): boolean {
  try {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  } catch {
    // Старый браузер без matchMedia — не предлагаем, карточка работает и так.
    return false;
  }
}

export function readNotifyOffered(storage: KeyValueStorage | null): boolean {
  return storage
    ? readJson(storage, WAITING_NOTIFY_OFFERED_KEY) === true
    : false;
}

export function rememberNotifyOffered(storage: KeyValueStorage | null): void {
  if (storage) writeJson(storage, WAITING_NOTIFY_OFFERED_KEY, true);
}

/** Пустой список или битое значение — одно и то же: вспоминать нечего. */
function readJson(storage: KeyValueStorage, name: string): unknown {
  try {
    const raw = storage.getItem(name);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    // Хранилище закрыто или значение испорчено руками — начинаем с чистого.
    return null;
  }
}

function writeJson(
  storage: KeyValueStorage,
  name: string,
  value: unknown
): boolean {
  try {
    storage.setItem(name, JSON.stringify(value));
    return true;
  } catch {
    // Хранилище недоступно: вкладка продолжит работать сама по себе.
    return false;
  }
}

export function readDismissedWaiting(storage: KeyValueStorage): Set<string> {
  const list = readJson(storage, WAITING_DISMISSED_KEY);
  return new Set(
    Array.isArray(list)
      ? list.filter((key): key is string => typeof key === "string")
      : []
  );
}

export function rememberDismissedWaiting(
  storage: KeyValueStorage,
  key: string
): Set<string> {
  const next = readDismissedWaiting(storage);
  next.delete(key);
  next.add(key);
  writeJson(storage, WAITING_DISMISSED_KEY, [...next].slice(-MAX_REMEMBERED));
  return next;
}

type AlertedMap = Record<string, string>;

function readAlerted(storage: KeyValueStorage): AlertedMap {
  const value = readJson(storage, WAITING_ALERTED_KEY);
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const result: AlertedMap = {};
  for (const [key, tab] of Object.entries(value as Record<string, unknown>)) {
    if (typeof tab === "string") result[key] = tab;
  }
  return result;
}

/**
 * Застолбить звонок по периоду ожидания за этой вкладкой. false — уже
 * позвонила другая. Хранилище не пишется — звоним сами: пусть лучше две
 * вкладки, чем ни одной.
 */
export function claimWaitingAlert(
  storage: KeyValueStorage,
  key: string,
  tabId: string
): boolean {
  const alerted = readAlerted(storage);
  const owner = alerted[key];
  if (owner && owner !== tabId) return false;
  if (owner === tabId) return true;
  const entries = Object.entries(alerted).slice(-(MAX_REMEMBERED - 1));
  // Не записалось — всё равно звоним: пусть лучше две вкладки, чем ни одной.
  writeJson(
    storage,
    WAITING_ALERTED_KEY,
    Object.fromEntries([...entries, [key, tabId]])
  );
  return true;
}

/** A failed/blocked playback must not consume this episode's sound forever. */
export function releaseWaitingAlertClaim(
  storage: KeyValueStorage,
  key: string,
  tabId: string
): void {
  const alerted = readAlerted(storage);
  if (alerted[key] !== tabId) return;
  delete alerted[key];
  writeJson(storage, WAITING_ALERTED_KEY, alerted);
}

/**
 * Проверка после паузы: две вкладки могли застолбить одно и то же почти
 * одновременно. Звонит та, чья запись осталась последней. Хранилище не
 * читается — звоним сами.
 */
export function ownsWaitingAlert(
  storage: KeyValueStorage,
  key: string,
  tabId: string
): boolean {
  try {
    const raw = storage.getItem(WAITING_ALERTED_KEY);
    if (raw === null) return true;
  } catch {
    return true;
  }
  const owner = readAlerted(storage)[key];
  return owner === undefined || owner === tabId;
}

/**
 * Скрытая вкладка звонит не сразу. Опрос идёт и в скрытых вкладках, и без
 * паузы звонок забирала бы та, что спросила сервер первой, — часто фоновая,
 * которую человек ни разу не трогал: браузер держит её звук спящим, и сигнал
 * пропал бы. Вкладка на экране опрашивает раз в 15 секунд и за эту паузу
 * успевает позвонить сама — тогда фоновая не показывает ещё и системное
 * уведомление поверх карточки, которую человек и так видит.
 */
export const HIDDEN_TAB_ALERT_DELAY_MS = 20_000;
/** Вкладке, которую ни разу не трогали, — ещё дольше: пусть первой будет та, что может звучать. */
export const UNTOUCHED_TAB_EXTRA_DELAY_MS = 5_000;

/** Через сколько эта вкладка сама звонит по новому ожиданию; 0 — сразу. */
export function waitingAlertDelayMs(input: {
  pageVisible: boolean;
  hasBeenActive: boolean;
}): number {
  if (input.pageVisible) return 0;
  return (
    HIDDEN_TAB_ALERT_DELAY_MS +
    (input.hasBeenActive ? 0 : UNTOUCHED_TAB_EXTRA_DELAY_MS)
  );
}

/**
 * Из живой встречи — только в новую вкладку. Переход в этой же вкладке
 * вывел бы рекрутёра из идущего разговора.
 */
export function waitingOpenMode(pathname: string): "new-tab" | "same-tab" {
  return isVideoLivePath(pathname) ? "new-tab" : "same-tab";
}

/**
 * Номер интервью, чья комната рекрутёра открыта по этому адресу
 * (`/video-interview/41/room`, строка запроса не в счёт); null — это не она.
 */
export function openRoomInterviewId(pathOrLink: string): number | null {
  const pathname = pathOrLink.split(/[?#]/, 1)[0];
  const match = /^\/video-interview\/(\d+)\/room\/?$/.exec(pathname);
  return match ? Number(match[1]) : null;
}

/**
 * An open camera-check screen is not a joined meeting. Keep its waiting
 * notice and sound, and put it first when the room can display one card.
 * The current-room action uses the existing join button instead of a new tab.
 */
export function waitingForThisPage(
  items: readonly CandidateWaitingPopup[],
  pathname: string
): CandidateWaitingPopup[] {
  const here = openRoomInterviewId(pathname);
  return here === null
    ? [...items]
    : [...items].sort(
        (a, b) =>
          Number(b.interviewId === here) - Number(a.interviewId === here)
      );
}

/**
 * Что делает «Войти в комнату» с этой страницы. "here" — эта комната уже
 * открыта в этой вкладке (например, клик по системному уведомлению, пока
 * рекрутёр проверяет камеру): никуда не ведём и копию не открываем.
 */
export function waitingEnterTarget(
  pathname: string,
  link: string
): "here" | "new-tab" | "same-tab" {
  const here = openRoomInterviewId(pathname);
  if (here !== null && here === openRoomInterviewId(link)) return "here";
  return waitingOpenMode(pathname);
}

/**
 * «с 14:03» — по часам компьютера рекрутёра. Пояс передают явно только тесты:
 * иначе результат зависел бы от машины, на которой их гоняют.
 */
export function waitingSinceLabel(sinceIso: string, timeZone?: string): string {
  const at = new Date(sinceIso);
  if (Number.isNaN(at.getTime())) return "";
  return `с ${at.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", timeZone })}`;
}

/**
 * Предлагать ли «Включить уведомления на компьютере». Только на компьютере
 * (на телефоне такие уведомления работают лишь у приложения на экране
 * «Домой»), только пока браузер ещё не спрашивал и только один раз.
 */
export function shouldOfferDesktopNotifications(input: {
  supported: boolean;
  permission: NotificationPermission | null;
  alreadyOffered: boolean;
  desktop: boolean;
}): boolean {
  return (
    input.supported &&
    input.permission === "default" &&
    !input.alreadyOffered &&
    input.desktop
  );
}

/** Системное уведомление: только имя и вакансия — оно видно и на заблокированном экране. */
export function waitingSystemNotificationText(
  item: Pick<
    CandidateWaitingPopup,
    "candidateName" | "vacancyTitle" | "companyName"
  >
): {
  title: string;
  body: string;
} {
  const who = item.candidateName.trim() || "Кандидат";
  const what = item.vacancyTitle.trim();
  return {
    title: "Вас ждут в комнате",
    body: `${what ? `${who} · ${what}` : who}. Нажмите, чтобы войти`,
  };
}

/** Текст карточки в CRM — те же слова, что в Telegram и в системном уведомлении. */
export function waitingCardText(
  item: CandidateWaitingPopup,
  timeZone?: string
): {
  title: string;
  subtitle: string;
  since: string;
} {
  return {
    title: candidateWaitingTitle(item.candidateName),
    subtitle: candidateWaitingSubtitle(item.vacancyTitle, item.companyName),
    since: waitingSinceLabel(item.since, timeZone),
  };
}

const systemNotificationTag = (key: string) => `candidate-waiting:${key}`;
const pageNotifications = new Map<string, Notification>();

/** Браузер знает про уведомления и человек их уже разрешил. */
export function systemNotificationsGranted(): boolean {
  return (
    typeof Notification !== "undefined" && Notification.permission === "granted"
  );
}

/**
 * Показать системное уведомление, если человек уже разрешил их браузеру.
 *
 * Сначала уведомление самой страницы: на компьютере оно работает везде, а
 * клик по нему возвращает в эту вкладку и ведёт в комнату тем же путём, что
 * кнопка на карточке (из живой встречи — в новую вкладку). Через service
 * worker — только там, где страница показывать не умеет (Chrome на Android,
 * приложение на экране «Домой» у iPhone): клик тогда обрабатывает
 * `candidate-waiting-sw.js` и открывает комнату в новом окне. Не наоборот,
 * потому что обновление worker ждёт кнопки «Обновить» (registerType: prompt),
 * и у тех, кто её ещё не нажал, клик по уведомлению worker ничего бы не делал.
 */
export async function showWaitingSystemNotification(
  item: CandidateWaitingPopup,
  onPageClick: () => void
): Promise<void> {
  if (!systemNotificationsGranted()) return;
  const { title, body } = waitingSystemNotificationText(item);
  const tag = systemNotificationTag(item.key);
  try {
    const notification = new Notification(title, {
      body,
      tag,
      icon: "/icon-192.png",
      requireInteraction: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
      onPageClick();
    };
    notification.onclose = () => pageNotifications.delete(item.key);
    pageNotifications.set(item.key, notification);
    return;
  } catch (error) {
    // Телефонный Chrome запрещает уведомления страницы — пробуем через worker.
    console.warn("[CandidateWaiting] уведомление страницы не показано:", error);
  }
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    if (!registration?.active) return;
    await registration.showNotification(title, {
      body,
      tag,
      icon: "/icon-192.png",
      requireInteraction: true,
      data: { kind: "candidate_waiting", url: item.link },
    });
  } catch (error) {
    // Карточка в CRM и Telegram всё равно дойдут.
    console.warn(
      "[CandidateWaiting] уведомление через service worker не показано:",
      error
    );
  }
}

/**
 * Периоды, по которым эта страница сейчас держит своё системное уведомление.
 * Карточки пересоздаются при переходе в живую встречу и обратно (другая
 * обёртка экрана), и новый экземпляр берёт эти уведомления на себя, чтобы
 * закрыть оставшиеся без карточки.
 */
export function openWaitingPageNotificationKeys(): string[] {
  return [...pageNotifications.keys()];
}

/** Кандидат ушёл или рекрутёр вошёл — системное уведомление больше не нужно. */
export async function closeWaitingSystemNotification(
  key: string
): Promise<void> {
  pageNotifications.get(key)?.close();
  pageNotifications.delete(key);
  if (!systemNotificationsGranted()) return;
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    const shown = await registration?.getNotifications({
      tag: systemNotificationTag(key),
    });
    shown?.forEach(notification => notification.close());
  } catch (error) {
    console.warn("[CandidateWaiting] уведомление не закрыто:", error);
  }
}
